import { useCallback, useEffect, useRef, useState } from 'react'

// Safety fallback only; normal end path should unfreeze immediately.
const INTERACTION_FREEZE_TIMEOUT_MS = 800

export const useInteractionFreeze = () => {
  const [isInteracting, setIsInteracting] = useState(false)
  const timeoutRef = useRef<number | undefined>(undefined)

  const clearFreezeTimer = useCallback(() => {
    window.clearTimeout(timeoutRef.current)
  }, [])

  const handleInteractionEnd = useCallback(() => {
    clearFreezeTimer()
    setIsInteracting(false)
  }, [clearFreezeTimer])

  const handleInteractionStart = useCallback(() => {
    setIsInteracting(true)
    clearFreezeTimer()
    timeoutRef.current = window.setTimeout(
      () => setIsInteracting(false),
      INTERACTION_FREEZE_TIMEOUT_MS,
    )
  }, [clearFreezeTimer])

  useEffect(() => {
    const unfreeze = () => handleInteractionEnd()
    document.addEventListener('pointerup', unfreeze)
    document.addEventListener('pointercancel', unfreeze)
    const onVisible = () => {
      if (document.visibilityState === 'visible') unfreeze()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', unfreeze)
    return () => {
      document.removeEventListener('pointerup', unfreeze)
      document.removeEventListener('pointercancel', unfreeze)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', unfreeze)
      clearFreezeTimer()
    }
  }, [handleInteractionEnd, clearFreezeTimer])

  return { isInteracting, handleInteractionStart, handleInteractionEnd }
}
